import { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import PropTypes from "prop-types";
import axios from "axios";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import useAuth from "../../../useAuth";
import {
  navbar,
  navbarContainer,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";
import {
  useVisionUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";
import logo from "assets/images/logo.png";
import { useMediaQuery, useTheme } from "@mui/material";

function DashboardNavbar({ absolute, light, isMini }) {
  useAuth();
  const isAuthenticated = useAuth();
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useVisionUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const [sites, setSites] = useState([]);
  const [userName, setUserName] = useState("Sign in");
  const location = useLocation();
  const currentSiteId = location.pathname.split("/")[2];
  const currentRegionId = location.pathname.split("/")[2];
  const [regions, setRegions] = useState([]);
  const email = localStorage.getItem("email");
  const userToken = localStorage.getItem("token");
  const userId = localStorage.getItem("user_id");

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!email || !userToken) {
        window.location.href = "/sign-in";
        return null;
      }
      try {
        const response = await axios.get(
          `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/user/profile`,
          {
            headers: { Authorization: `${userToken}`, "Content-Type": "application/json" },
            params: { email },
          }
        );
        setUserName(response.data.first_name);
        localStorage.setItem("name", response.data.first_name);
        const userId = response.data.user_id;
        fetchUserSites(userId);
        fetchUserRegions(userId);
      } catch (error) {
        console.error("Error fetching user profile:", error.response || error.message);
      }
    };

    const fetchUserSites = async (userId) => {
      try {
        const response = await axios.get(
          "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/user/sites",
          {
            headers: { Authorization: userToken },
            params: { user_id: userId },
          }
        );
        const fetchedSites = response.data || [];
        setSites(fetchedSites);
      } catch (error) {
        console.error("Error fetching sites:", error.message);
      }
    };

    const fetchUserRegions = async (userId) => {
      try {
        const response = await axios.get(
          "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/user/regions",
          {
            headers: { Authorization: userToken },
            params: { user_id: userId },
          }
        );
        const formattedRegions = (response.data || []).map((region) => ({
          region_id: region[0],
          name: region[1],
          description: region[2],
          created_at: region[3],
          updated_at: region[4],
        }));
        setRegions(formattedRegions);
      } catch (error) {
        console.error("Error fetching regions:", error.message);
      }
    };

    fetchUserProfile();
  }, [email, userToken, location.pathname]);

  useEffect(() => {
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();

    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);

  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    ></Menu>
  );

  const isSitePage = () => {
    const path = location.pathname;
    return (
      path.startsWith("/site-dashboard") ||
      path.startsWith("/train-load-export") ||
      path.startsWith("/train-load-recipients") ||
      path.startsWith("/ppm-data-export") ||
      path.startsWith("/ppm/") ||
      path.startsWith("/engineer-page") ||
      path.startsWith("/data-export") ||
      path.startsWith("/stock-items")
    );
  };

  const roleId = parseInt(localStorage.getItem("role_id"), 10);
  const canViewAreaDashboard = roleId === 1;
  const canViewNationalDashboard = roleId === 5;

  const isActiveAreaDashboard = location.pathname.startsWith("/area-dashboard");
  const isActiveNationalDashboard = location.pathname.startsWith("/national-dashboard");

  // --- Filter sites logic (like Configurator)
  const filteredSites = sites.filter((site) => {
    if (
      location.pathname.includes("train-load-export") ||
      location.pathname.includes("train-load-recipients")
    ) {
      return site.dashboard_config?.includes("Train Loading");
    }
    return true; // show all sites otherwise
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <VuiBox display="flex" alignItems="center">
          <img src={logo} alt="logo" style={{ height: "40px", marginRight: "10px" }} />
        </VuiBox>

        <VuiBox
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: "center",
            flexGrow: 1,
            overflowX: isMobile ? "hidden" : "auto",
            flexWrap: isMobile ? "wrap" : "nowrap",
            justifyContent: isMobile ? "flex-start" : "center",
            marginLeft: isMobile ? 0 : "auto",
            marginRight: isMobile ? 0 : "auto",
          }}
        >
          {canViewAreaDashboard && (
            <VuiButton
              variant="gradient"
              sx={(theme) => ({
                background: isActiveAreaDashboard
                  ? "linear-gradient(90deg, #00A9E2, #9B9C9B)"
                  : "transparent",
                color: isActiveAreaDashboard ? theme.palette.white.main : theme.palette.text.main,
                "&:hover": {
                  color: theme.palette.white.main,
                  background: "linear-gradient(90deg, #00A9E2, #9B9C9B)",
                },
                mx: 1,
                mb: isMobile ? 1 : 0,
                minWidth: "fit-content",
              })}
              onClick={() => {
                if (regions.length > 0) {
                  const firstRegionId = regions[0].region_id;
                  window.location.href = `/area-dashboard/${firstRegionId}`;
                }
              }}
            >
              Area Dashboard
            </VuiButton>
          )}

          {canViewNationalDashboard && (
            <VuiButton
              variant="gradient"
              sx={(theme) => ({
                background: isActiveNationalDashboard
                  ? "linear-gradient(90deg, #00A9E2, #9B9C9B)"
                  : "transparent",
                color: isActiveNationalDashboard
                  ? theme.palette.white.main
                  : theme.palette.text.main,
                "&:hover": {
                  color: theme.palette.white.main,
                  background: "linear-gradient(90deg, #00A9E2, #9B9C9B)",
                },
                mx: 1,
                mb: isMobile ? 1 : 0,
                minWidth: "fit-content",
              })}
              onClick={() => {
                window.location.href = `/national-dashboard`;
              }}
            >
              National Dashboard
            </VuiButton>
          )}

          {/* Render regions if on area dashboard, otherwise render sites */}
          {isActiveAreaDashboard
            ? regions.map((region) => {
                const isActive = currentRegionId === region.region_id.toString();
                return (
                  <Link
                    to={`/area-dashboard/${region.region_id}`}
                    key={region.region_id}
                    style={{ textDecoration: "none" }}
                  >
                    <VuiButton
                      variant="gradient"
                      sx={(theme) => ({
                        background: isActive
                          ? "linear-gradient(90deg, #00A9E2, #9B9C9B)"
                          : "transparent",
                        color: isActive ? theme.palette.white.main : theme.palette.text.main,
                        "&:hover": {
                          color: theme.palette.white.main,
                          background: "linear-gradient(90deg, #00A9E2, #9B9C9B)",
                        },
                        mx: 1,
                        mb: isMobile ? 1 : 0,
                        minWidth: "fit-content",
                        whiteSpace: "nowrap",
                      })}
                    >
                      {region.name}
                    </VuiButton>
                  </Link>
                );
              })
            : isActiveNationalDashboard
            ? regions.map((region) => {
                const isActive = currentRegionId === region.region_id.toString();
                return (
                  <Link
                    to={`/area-dashboard/${region.region_id}`}
                    key={region.region_id}
                    style={{ textDecoration: "none" }}
                  >
                    <VuiButton
                      variant="gradient"
                      sx={(theme) => ({
                        background: isActive
                          ? "linear-gradient(90deg, #00A9E2, #9B9C9B)"
                          : "transparent",
                        color: isActive ? theme.palette.white.main : theme.palette.text.main,
                        "&:hover": {
                          color: theme.palette.white.main,
                          background: "linear-gradient(90deg, #00A9E2, #9B9C9B)",
                        },
                        mx: 1,
                        mb: isMobile ? 1 : 0,
                        minWidth: "fit-content",
                        whiteSpace: "nowrap",
                      })}
                    >
                      {region.name}
                    </VuiButton>
                  </Link>
                );
              })
            : filteredSites.map((site) => {
                const isActive = isSitePage() && currentSiteId === site.site_id.toString();
                const isCurrentSite = currentSiteId === site.site_id.toString();

                const newUrl = isCurrentSite
                  ? `/site-dashboard/${site.site_id}`
                  : location.pathname.includes("train-load-export")
                  ? `/train-load-export/${site.site_id}`
                  : location.pathname.includes("train-load-recipients")
                  ? `/train-load-recipients/${site.site_id}`
                  : location.pathname.includes("ppm-data-export")
                  ? `/ppm-data-export/${site.site_id}`
                  : location.pathname.includes("ppm")
                  ? `/ppm/${site.site_id}/section-list`
                  : location.pathname.includes("engineer-page")
                  ? `/engineer-page/${site.site_id}`
                  : location.pathname.includes("data-export")
                  ? `/data-export/${site.site_id}`
                  : location.pathname.includes("stock-items")
                  ? `/stock-items/${site.site_id}`
                  : `/site-dashboard/${site.site_id}`;

                return (
                  <Link to={newUrl} key={site.site_id} style={{ textDecoration: "none" }}>
                    <VuiButton
                      key={site.site_id}
                      variant="gradient"
                      sx={(theme) => ({
                        background: isActive
                          ? "linear-gradient(90deg, #00A9E2, #9B9C9B)"
                          : "transparent",
                        color: isActive ? theme.palette.white.main : theme.palette.text.main,
                        "&:hover": {
                          color: theme.palette.white.main,
                          background: "linear-gradient(90deg, #00A9E2, #9B9C9B)",
                        },
                        mx: 1,
                        mb: isMobile ? 1 : 0,
                        minWidth: "fit-content",
                        whiteSpace: "nowrap",
                      })}
                    >
                      {site.name}
                    </VuiButton>
                  </Link>
                );
              })}
        </VuiBox>

        <VuiBox color={light ? "white" : "inherit"}>
          <Link to="/settings">
            <IconButton sx={navbarIconButton} size="small">
              <Icon
                sx={({ palette: { dark, white } }) => ({
                  color: light ? white.main : dark.main,
                })}
              >
                account_circle
              </Icon>
              <VuiTypography variant="button" fontWeight="medium" color={light ? "white" : "dark"}>
                {userName}
              </VuiTypography>
            </IconButton>
          </Link>
          <IconButton
            size="small"
            color="inherit"
            sx={navbarMobileMenu}
            onClick={handleMiniSidenav}
          ></IconButton>
          <IconButton
            size="small"
            color="inherit"
            sx={navbarIconButton}
            onClick={handleConfiguratorOpen}
          >
            <Icon>menu</Icon>
          </IconButton>
          {renderMenu()}
        </VuiBox>
      </Toolbar>
    </AppBar>
  );
}

DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;